import React, { useState, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import api from '../services/api';
import s from './AlteracaoMassaProdutos.module.css';
import useBuscaProdutosF3, { FILTROS_F3_VAZIOS } from '../hooks/useBuscaProdutosF3';
import ModalBuscaProdutos from '../components/pdv/ModalBuscaProdutos';

// ── Constantes tributárias ────────────────────────────────────────────────────

const TIPOS_PRODUTO = [
  { value: 'mercadoria',         label: 'Mercadoria' },
  { value: 'serviço',            label: 'Serviço' },
  { value: 'fabricação própria', label: 'Fabricação Própria' },
  { value: 'kit/conjunto',       label: 'Kit/Conjunto' },
  { value: 'insumo',             label: 'Insumo' },
];

// CST (regime normal) é 2 dígitos ("00".."90") — ver mesmo achado/correção
// em CadastroProdutos.jsx#CSTS_ICMS e TiposMovimentoFiscal.jsx#CSTS_ICMS
// (2026-09-16). CSOSN (101-900) é 3 dígitos mesmo, continuam certos.
const CSTS_ICMS = [
  { v: '00', l: '00 — Tributada integralmente' },
  { v: '10', l: '10 — Tributada e com cobrança ICMS-ST' },
  { v: '20', l: '20 — Com redução de base de cálculo' },
  { v: '30', l: '30 — Isenta/não tributada e com cobrança ICMS-ST' },
  { v: '40', l: '40 — Isenta' },
  { v: '41', l: '41 — Não tributada' },
  { v: '50', l: '50 — Com suspensão' },
  { v: '51', l: '51 — Com diferimento' },
  { v: '60', l: '60 — ICMS cobrado anteriormente por ST' },
  { v: '70', l: '70 — Com redução de B.C. e cobrança ICMS-ST' },
  { v: '90', l: '90 — Outras' },
  { v: '101', l: '101 — Tributada pelo SN com permissão de crédito' },
  { v: '102', l: '102 — Tributada pelo SN sem permissão de crédito' },
  { v: '103', l: '103 — Isenção ICMS no SN por receita bruta' },
  { v: '201', l: '201 — SN c/ crédito e c/ ICMS-ST' },
  { v: '202', l: '202 — SN s/ crédito e c/ ICMS-ST' },
  { v: '203', l: '203 — Isenção no SN e com ICMS-ST' },
  { v: '300', l: '300 — Imune' },
  { v: '400', l: '400 — Não tributada pelo Simples Nacional' },
  { v: '500', l: '500 — ICMS cobrado anteriormente por ST ou antecipação' },
  { v: '900', l: '900 — Outros (SN)' },
];

const MODS_BC_ICMS = [
  { v: '0', l: '0 — Margem Valor Agregado (%)' },
  { v: '1', l: '1 — Pauta (Valor)' },
  { v: '2', l: '2 — Preço Tabelado Máximo (valor)' },
  { v: '3', l: '3 — Valor da Operação' },
];

const MODS_BC_ICMS_ST = [
  { v: '0', l: '0 — Preço Tabelado ou Máximo Sugerido' },
  { v: '1', l: '1 — Lista Negativa (valor)' },
  { v: '2', l: '2 — Lista Positiva (valor)' },
  { v: '3', l: '3 — Lista Neutra (valor)' },
  { v: '4', l: '4 — Margem Valor Agregado (%)' },
  { v: '5', l: '5 — Pauta (valor)' },
  { v: '6', l: '6 — Valor da Operação' },
];

const CSTS_PIS_COFINS_ENT = [
  { v: '01', l: '01 — Op. Tributável c/ Alíquota Básica' },
  { v: '02', l: '02 — Op. Tributável c/ Alíquota Diferenciada' },
  { v: '03', l: '03 — Op. Tributável por Unidade de Medida' },
  { v: '04', l: '04 — Op. Monofásica — Revenda a Alíquota Zero' },
  { v: '05', l: '05 — Op. por Substituição Tributária' },
  { v: '06', l: '06 — Op. Tributável a Alíquota Zero' },
  { v: '07', l: '07 — Op. Isenta da Contribuição' },
  { v: '08', l: '08 — Op. sem Incidência da Contribuição' },
  { v: '09', l: '09 — Op. com Suspensão da Contribuição' },
  { v: '49', l: '49 — Outras Operações de Entrada' },
  { v: '50', l: '50 — Créd. Vinculado a Receita Tributada' },
  { v: '99', l: '99 — Outras Operações de Entrada' },
];

const CSTS_PIS_COFINS_SAI = [
  { v: '01', l: '01 — Op. Tributável c/ Alíquota Básica' },
  { v: '02', l: '02 — Op. Tributável c/ Alíquota Diferenciada' },
  { v: '03', l: '03 — Op. Tributável por Unidade de Medida' },
  { v: '04', l: '04 — Op. Monofásica — Revenda a Alíquota Zero' },
  { v: '05', l: '05 — Op. por Substituição Tributária' },
  { v: '06', l: '06 — Op. Tributável a Alíquota Zero' },
  { v: '07', l: '07 — Op. Isenta da Contribuição' },
  { v: '08', l: '08 — Op. sem Incidência da Contribuição' },
  { v: '09', l: '09 — Op. com Suspensão da Contribuição' },
  { v: '49', l: '49 — Outras Operações de Saída' },
  { v: '99', l: '99 — Outras Operações' },
];

const ORIGENS = [
  { v: 0, l: '0 – Nacional' },
  { v: 1, l: '1 – Estrangeira (importação direta)' },
  { v: 2, l: '2 – Estrangeira (mercado interno)' },
  { v: 3, l: '3 – Nacional, import. > 40%' },
  { v: 4, l: '4 – Nacional, processos básicos' },
  { v: 5, l: '5 – Nacional, import. < 40%' },
  { v: 6, l: '6 – Estrangeira s/ similar (direta)' },
  { v: 7, l: '7 – Estrangeira s/ similar (interno)' },
  { v: 8, l: '8 – Nacional, import. > 70%' },
];

const TIPO_FISCAL_OPTS = [
  { v: '00', l: '00 – Tributada integralmente' },
  { v: '10', l: '10 – Tributada + ST' },
  { v: '20', l: '20 – Com redução de BC' },
  { v: '30', l: '30 – Isenta/não-trib. + ST' },
  { v: '40', l: '40 – Isenta' },
  { v: '41', l: '41 – Não tributada' },
  { v: '50', l: '50 – Suspensão' },
  { v: '51', l: '51 – Diferimento' },
  { v: '60', l: '60 – ICMS cobrado por ST anteriormente' },
  { v: '70', l: '70 – Redução BC + ST' },
  { v: '90', l: '90 – Outras' },
  { v: '101', l: '101 – SN c/ permissão de crédito' },
  { v: '102', l: '102 – SN s/ permissão de crédito' },
  { v: '103', l: '103 – SN, isenção por receita bruta' },
  { v: '300', l: '300 – Imune' },
  { v: '400', l: '400 – Não tributada pelo SN' },
  { v: '500', l: '500 – ICMS cobrado por ST (SN)' },
  { v: '900', l: '900 – Outros (SN)' },
];

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
             'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

// CST de IBS/CBS — Reforma Tributária (NT 2025.002, obrigatoriedade
// 01/01/2027). Mesma lista de CadastroProdutos.jsx — confira a versão
// vigente no portal da NF-e antes de emitir documento fiscal de verdade.
const CSTS_IBSCBS = [
  { v: '000', l: '000 — Tributação integral' },
  { v: '010', l: '010 — Tributação com alíquota reduzida' },
  { v: '011', l: '011 — Tributação com alíquota reduzida e diferimento' },
  { v: '020', l: '020 — Tributação com redução de base de cálculo' },
  { v: '200', l: '200 — Isenção' },
  { v: '210', l: '210 — Imunidade' },
  { v: '220', l: '220 — Suspensão' },
  { v: '221', l: '221 — Suspensão — Diferimento' },
  { v: '400', l: '400 — Não incidência' },
  { v: '410', l: '410 — Não incidência — Zona Franca de Manaus / Áreas de Livre Comércio' },
  { v: '500', l: '500 — Diferimento' },
  { v: '510', l: '510 — Diferimento — ZFM / ALC' },
  { v: '550', l: '550 — Suspensão do IBS/CBS' },
  { v: '620', l: '620 — Tributação monofásica' },
  { v: '900', l: '900 — Outros' },
];

// ── Label map para o modal ────────────────────────────────────────────────────
const LABELS = {
  // produto
  fornecedor_id:'Fornecedor', marca_id:'Marca', grupo_id:'Grupo', subgrupo_id:'Subgrupo',
  tipo:'Tipo', inativo:'Inativo',
  ncm:'NCM', cest:'CEST', origem_fiscal:'Origem', tipo_fiscal:'Tipo Fiscal',
  unidade_tributavel:'Un. Tributável', fator_conversao_trib:'Fator Conv. Trib.',
  unidade_tributavel_diferente:'Un. Trib. Diferente',
  peso_kg:'Peso (kg)', comissao_pct:'Comissão %', desconto_maximo_pct:'Desc. Máximo %', margem_pct:'Margem %',
  pesado_no_caixa:'Pesado no caixa', possui_acrescimo:'Possui acréscimo', producao_propria:'Produção própria',
  produto_balanca:'Produto de balança', recebido_em_consignacao:'Recebido em consignação',
  permite_alterar_qtd_preco:'Permite alterar quantidade/preço no PDV', item_cozinha:'Item de Cozinha',
  marcador:'Marcador', composto:'Composto',
  // fiscal entrada (prefixo fe_)
  fe_icms_cst:'Ent. ICMS – Situação Trib.', fe_icms_mod_bc:'Ent. ICMS – Modalidade BC',
  fe_icms_red_bc_perc:'Ent. ICMS – Redução BC %', fe_icms_aliq_perc:'Ent. ICMS – Alíquota %',
  fe_icms_st_mod_bc:'Ent. ICMS-ST – Modalidade', fe_icms_st_red_bc_perc:'Ent. ICMS-ST – Redução %',
  fe_icms_st_mva_perc:'Ent. ICMS-ST – MVA %', fe_icms_st_aliq_perc:'Ent. ICMS-ST – Alíquota %',
  fe_icms_st_ret_bc:'Ent. ICMS-ST Ret. – BC R$', fe_icms_st_ret_valor:'Ent. ICMS-ST Ret. – Valor R$',
  fe_pis_cst:'Ent. PIS – Situação Trib.', fe_pis_aliq_perc:'Ent. PIS – Alíquota %',
  fe_cofins_cst:'Ent. COFINS – Situação Trib.', fe_cofins_aliq_perc:'Ent. COFINS – Alíquota %',
  fe_ibs_aliq_perc:'Ent. IBS – Alíquota %', fe_ibs_uf_destino:'Ent. IBS – UF Destino',
  fe_cbs_aliq_perc:'Ent. CBS – Alíquota %', fe_is_incide:'Ent. IS – Incide',
  fe_is_aliq_perc:'Ent. IS – Alíquota %',
  // fiscal saida (prefixo fs_)
  fs_icms_cst:'Saí. ICMS – Situação Trib.', fs_icms_mod_bc:'Saí. ICMS – Modalidade BC',
  fs_icms_red_bc_perc:'Saí. ICMS – Redução BC %', fs_icms_aliq_perc:'Saí. ICMS – Alíquota %',
  fs_icms_aliq_efet_perc:'Saí. ICMS – Alíq. Efetiva %', fs_icms_dif_perc:'Saí. ICMS – Diferimento %',
  fs_icms_st_mod_bc:'Saí. ICMS-ST – Modalidade', fs_icms_st_red_bc_perc:'Saí. ICMS-ST – Redução %',
  fs_icms_st_mva_perc:'Saí. ICMS-ST – MVA %', fs_icms_st_aliq_perc:'Saí. ICMS-ST – Alíquota %',
  fs_icms_st_ret_bc:'Saí. ICMS-ST Ret. – BC R$', fs_icms_st_ret_valor:'Saí. ICMS-ST Ret. – Valor R$',
  fs_pis_cst:'Saí. PIS – Situação Trib.', fs_pis_aliq_perc:'Saí. PIS – Alíquota %',
  fs_pis_nat_rec:'Saí. PIS – Nat. Receita',
  fs_cofins_cst:'Saí. COFINS – Situação Trib.', fs_cofins_aliq_perc:'Saí. COFINS – Alíquota %',
  fs_cofins_nat_rec:'Saí. COFINS – Nat. Receita',
  fs_ibscbs_cst:'Saí. CST IBS/CBS', fs_ibscbs_cclasstrib:'Saí. cClassTrib',
  fs_ibs_aliq_perc:'Saí. IBS – Alíquota %', fs_ibs_uf_destino:'Saí. IBS – UF Destino',
  fs_ibs_red_bc_perc:'Saí. IBS – Redução BC %', fs_ibs_dif_perc:'Saí. IBS – Diferimento %',
  fs_ibs_cred_pres_codigo:'Saí. IBS – Créd. Presumido (cód.)', fs_ibs_cred_pres_perc:'Saí. IBS – Créd. Presumido %',
  fs_cbs_aliq_perc:'Saí. CBS – Alíquota %',
  fs_cbs_red_bc_perc:'Saí. CBS – Redução BC %', fs_cbs_dif_perc:'Saí. CBS – Diferimento %',
  fs_cbs_cred_pres_codigo:'Saí. CBS – Créd. Presumido (cód.)', fs_cbs_cred_pres_perc:'Saí. CBS – Créd. Presumido %',
  fs_is_incide:'Saí. IS – Incide',
  fs_is_aliq_perc:'Saí. IS – Alíquota %',
};

// ── Estado inicial ────────────────────────────────────────────────────────────
const mk = (...campos) => Object.fromEntries(campos.map(c => [c, false]));
const mv = (entries) => Object.fromEntries(entries);

const MARCADOS_VAZIO = {
  ...mk('fornecedor_id','marca_id','grupo_id','subgrupo_id','tipo','inativo'),
  ...mk('ncm','cest','origem_fiscal','tipo_fiscal','unidade_tributavel','fator_conversao_trib','unidade_tributavel_diferente'),
  ...mk('peso_kg','comissao_pct','desconto_maximo_pct','margem_pct'),
  ...mk('pesado_no_caixa','possui_acrescimo','producao_propria','produto_balanca','recebido_em_consignacao','permite_alterar_qtd_preco','item_cozinha'),
  ...mk('marcador','composto'),
  // fiscal entrada
  ...mk('fe_icms_cst','fe_icms_mod_bc','fe_icms_red_bc_perc','fe_icms_aliq_perc'),
  ...mk('fe_icms_st_mod_bc','fe_icms_st_red_bc_perc','fe_icms_st_mva_perc','fe_icms_st_aliq_perc'),
  ...mk('fe_icms_st_ret_bc','fe_icms_st_ret_valor'),
  ...mk('fe_pis_cst','fe_pis_aliq_perc'),
  ...mk('fe_cofins_cst','fe_cofins_aliq_perc'),
  ...mk('fe_ibs_aliq_perc','fe_ibs_uf_destino','fe_cbs_aliq_perc','fe_is_incide','fe_is_aliq_perc'),
  // fiscal saida
  ...mk('fs_icms_cst','fs_icms_mod_bc','fs_icms_red_bc_perc','fs_icms_aliq_perc','fs_icms_aliq_efet_perc','fs_icms_dif_perc'),
  ...mk('fs_icms_st_mod_bc','fs_icms_st_red_bc_perc','fs_icms_st_mva_perc','fs_icms_st_aliq_perc'),
  ...mk('fs_icms_st_ret_bc','fs_icms_st_ret_valor'),
  ...mk('fs_pis_cst','fs_pis_aliq_perc','fs_pis_nat_rec'),
  ...mk('fs_cofins_cst','fs_cofins_aliq_perc','fs_cofins_nat_rec'),
  ...mk('fs_ibscbs_cst','fs_ibscbs_cclasstrib'),
  ...mk('fs_ibs_aliq_perc','fs_ibs_uf_destino','fs_ibs_red_bc_perc','fs_ibs_dif_perc'),
  ...mk('fs_ibs_cred_pres_codigo','fs_ibs_cred_pres_perc'),
  ...mk('fs_cbs_aliq_perc','fs_cbs_red_bc_perc','fs_cbs_dif_perc'),
  ...mk('fs_cbs_cred_pres_codigo','fs_cbs_cred_pres_perc'),
  ...mk('fs_is_incide','fs_is_aliq_perc'),
};

const VALORES_VAZIO = {
  fornecedor_id:'', marca_id:'', grupo_id:'', subgrupo_id:'', tipo:'', inativo:false,
  ncm:'', cest:'', origem_fiscal:0, tipo_fiscal:'00',
  unidade_tributavel:'', fator_conversao_trib:1, unidade_tributavel_diferente:false,
  peso_kg:'', comissao_pct:'', desconto_maximo_pct:'', margem_pct:'',
  pesado_no_caixa:false, possui_acrescimo:false, producao_propria:false,
  produto_balanca:false, recebido_em_consignacao:false, permite_alterar_qtd_preco:false, item_cozinha:false,
  marcador:false, composto:false,
  // fiscal entrada
  fe_icms_cst:'', fe_icms_mod_bc:'', fe_icms_red_bc_perc:'', fe_icms_aliq_perc:'',
  fe_icms_st_mod_bc:'', fe_icms_st_red_bc_perc:'', fe_icms_st_mva_perc:'', fe_icms_st_aliq_perc:'',
  fe_icms_st_ret_bc:'', fe_icms_st_ret_valor:'',
  fe_pis_cst:'', fe_pis_aliq_perc:'',
  fe_cofins_cst:'', fe_cofins_aliq_perc:'',
  fe_ibs_aliq_perc:'', fe_ibs_uf_destino:'', fe_cbs_aliq_perc:'', fe_is_incide:false, fe_is_aliq_perc:'',
  // fiscal saida
  fs_icms_cst:'', fs_icms_mod_bc:'', fs_icms_red_bc_perc:'', fs_icms_aliq_perc:'',
  fs_icms_aliq_efet_perc:'', fs_icms_dif_perc:'',
  fs_icms_st_mod_bc:'', fs_icms_st_red_bc_perc:'', fs_icms_st_mva_perc:'', fs_icms_st_aliq_perc:'',
  fs_icms_st_ret_bc:'', fs_icms_st_ret_valor:'',
  fs_pis_cst:'', fs_pis_aliq_perc:'', fs_pis_nat_rec:'',
  fs_cofins_cst:'', fs_cofins_aliq_perc:'', fs_cofins_nat_rec:'',
  fs_ibscbs_cst:'', fs_ibscbs_cclasstrib:'',
  fs_ibs_aliq_perc:'', fs_ibs_uf_destino:'', fs_ibs_red_bc_perc:'', fs_ibs_dif_perc:'',
  fs_ibs_cred_pres_codigo:'', fs_ibs_cred_pres_perc:'',
  fs_cbs_aliq_perc:'', fs_cbs_red_bc_perc:'', fs_cbs_dif_perc:'',
  fs_cbs_cred_pres_codigo:'', fs_cbs_cred_pres_perc:'',
  fs_is_incide:false, fs_is_aliq_perc:'',
};

// ── CheckField genérico ───────────────────────────────────────────────────────
function CF({ campo, label, children, marcados, onToggle, fiscal }) {
  const cn = fiscal ? s.campoLinhaFiscal : s.campoLinha;
  const lb = fiscal ? s.campoLabelFiscal : s.campoLabel;
  const ip = fiscal ? s.campoInputFiscal : s.campoInput;
  return (
    <div className={cn}>
      <input type="checkbox" checked={!!marcados[campo]} onChange={() => onToggle(campo)} />
      <span className={lb}>{label}</span>
      <div className={ip}>{children}</div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function AlteracaoMassaProdutos() {
  const [buscaAtivada, setBuscaAtivada] = useState(false);
  const [produtos, setProdutos]       = useState([]);
  const [selecionados, setSelecionados] = useState(new Set());
  const [abaAtiva, setAbaAtiva]       = useState('geral');
  const [subAbaFiscal, setSubAbaFiscal] = useState('entrada');
  const [marcados, setMarcados]       = useState({ ...MARCADOS_VAZIO });
  const [valores, setValores]         = useState({ ...VALORES_VAZIO });
  const [status, setStatus]           = useState('');
  const [statusTipo, setStatusTipo]   = useState('');
  const [confirmando, setConfirmando] = useState(false);

  const { data: grupos = [] }       = useQuery({ queryKey: ['grupos'],       queryFn: () => api.get('/auxiliares/grupos').then(r => r.data) });
  const { data: subgrupos = [] }    = useQuery({ queryKey: ['subgrupos'],    queryFn: () => api.get('/auxiliares/subgrupos').then(r => r.data) });
  const { data: marcas = [] }       = useQuery({ queryKey: ['marcas'],       queryFn: () => api.get('/auxiliares/marcas').then(r => r.data) });
  const { data: fornecedores = [] } = useQuery({ queryKey: ['fornecedores'], queryFn: () => api.get('/auxiliares/fornecedores').then(r => r.data) });

  const setV = (campo, val) => setValores(v => ({ ...v, [campo]: val }));
  const toggleMarcado = useCallback(campo => setMarcados(m => ({ ...m, [campo]: !m[campo] })), []);

  // ── F3 — Busca Avançada de Produtos ──────────────────────────────────────
  // Substitui o antigo painel de filtros fixo: clicar em "Buscar" abre o
  // mesmo modal usado no PDV/Pedidos (com NCM, Tributação, Cód. barras,
  // Estoque negativo, Sem NCM etc. — todos os filtros realmente aplicando no
  // backend). O "Ok" do modal, aqui, traz TODOS os produtos que a busca
  // encontrou (não só um) e ACRESCENTA à grade — clicar em Buscar de novo com
  // outro filtro soma mais produtos à lista em vez de substituir.
  const buscaF3 = useBuscaProdutosF3({ semLimite: true }); // "Ok" usa TODOS os encontrados

  const confirmarTodos = (encontrados) => {
    setProdutos(prev => {
      const existentes = new Set(prev.map(p => p.id));
      const novos = encontrados.filter(p => !existentes.has(p.id));
      return [...prev, ...novos];
    });
    setBuscaAtivada(true);
    setStatus(`${encontrados.length} produto(s) encontrado(s) — adicionados à lista.`);
    setStatusTipo('');
    buscaF3.setModalF3(false);
  };

  const toggleSelecionado = id => setSelecionados(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selecionarTodos  = () => setSelecionados(new Set(produtos.map(p => p.id)));
  const desmarcarTodos   = () => setSelecionados(new Set());
  const todosSelecionados = produtos.length > 0 && selecionados.size === produtos.length;
  const qtdSelecionados   = selecionados.size;
  const camposMarcados    = Object.entries(marcados).filter(([, v]) => v).map(([k]) => k);

  // ── Montar payload para o backend ────────────────────────────────────────
  const montarPayload = () => {
    const alteracoes = { produto: {}, fiscal_entrada: {}, fiscal_saida: {} };
    for (const campo of camposMarcados) {
      const val = valores[campo] === '' ? null : valores[campo];
      if (campo.startsWith('fe_'))      alteracoes.fiscal_entrada[campo.slice(3)] = val;
      else if (campo.startsWith('fs_')) alteracoes.fiscal_saida[campo.slice(3)]   = val;
      else                              alteracoes.produto[campo]                  = val;
    }
    if (!Object.keys(alteracoes.produto).length)        delete alteracoes.produto;
    if (!Object.keys(alteracoes.fiscal_entrada).length) delete alteracoes.fiscal_entrada;
    if (!Object.keys(alteracoes.fiscal_saida).length)   delete alteracoes.fiscal_saida;
    return alteracoes;
  };

  const alteracaoMutation = useMutation({
    mutationFn: payload => api.put('/produtos/alteracao-massa', payload),
    onSuccess: async res => {
      setStatus(`${res.data.alterados} produto(s) alterado(s) com sucesso!`);
      setStatusTipo('ok');
      setSelecionados(new Set());
      setMarcados({ ...MARCADOS_VAZIO });
      setValores({ ...VALORES_VAZIO });
      await refrescarLista();
    },
    onError: err => {
      const msg = err.response?.data?.erro || err.message;
      setStatus('Erro: ' + msg); setStatusTipo('erro');
    },
    onSettled: () => setConfirmando(false),
  });

  const confirmarAplicar = () => {
    if (qtdSelecionados === 0)      { setStatus('Selecione ao menos um produto.'); setStatusTipo('erro'); return; }
    if (camposMarcados.length === 0){ setStatus('Marque ao menos um campo para alterar.'); setStatusTipo('erro'); return; }
    setConfirmando(true);
  };

  const executarAlteracao = () =>
    alteracaoMutation.mutate({ ids: Array.from(selecionados), alteracoes: montarPayload() });

  // Depois de aplicar a alteração em massa, atualiza a grade com os dados
  // frescos dos produtos JÁ listados (mesmos ids) — sem precisar redigitar
  // filtro nenhum, já que a lista pode ter vindo de várias buscas somadas.
  const refrescarLista = async () => {
    if (produtos.length === 0) return;
    try {
      const { data } = await api.get('/produtos', {
        params: { ids: produtos.map(p => p.id).join(','), todos: 'true' },
      });
      setProdutos(data);
    } catch { /* mantém a lista anterior se a atualização falhar */ }
  };

  const limparLista = () => {
    setProdutos([]); setBuscaAtivada(false);
    setSelecionados(new Set()); setStatus('');
  };

  const limparSelecao = () => {
    setSelecionados(new Set());
    setMarcados({ ...MARCADOS_VAZIO });
    setValores({ ...VALORES_VAZIO });
    setStatus('');
  };

  // ── Helpers de input ─────────────────────────────────────────────────────
  const inp = (campo, type = 'text', extra = {}) => ({
    type, ...extra,
    disabled: !marcados[campo],
    value: valores[campo] ?? '',
    onChange: e => setV(campo, type === 'number' ? e.target.value : e.target.value),
  });

  const sel = (campo) => ({
    disabled: !marcados[campo],
    value: valores[campo] ?? '',
    onChange: e => setV(campo, e.target.value),
  });

  const boo = (campo) => ({
    disabled: !marcados[campo],
    value: valores[campo] ? '1' : '0',
    onChange: e => setV(campo, e.target.value === '1'),
  });

  // ── Seção ICMS ───────────────────────────────────────────────────────────
  const secIcms = (pref, cstsOpts) => (
    <>
      <div className={s.secLabel}>ICMS</div>
      <CF campo={`${pref}icms_cst`} label="Situação Trib." marcados={marcados} onToggle={toggleMarcado} fiscal>
        <select {...sel(`${pref}icms_cst`)}>
          <option value="">-- Selecione --</option>
          {cstsOpts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      </CF>
      <CF campo={`${pref}icms_mod_bc`} label="Modalidade BC" marcados={marcados} onToggle={toggleMarcado} fiscal>
        <select {...sel(`${pref}icms_mod_bc`)}>
          <option value="">-- Selecione --</option>
          {MODS_BC_ICMS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      </CF>
      <CF campo={`${pref}icms_red_bc_perc`} label="Redução BC %" marcados={marcados} onToggle={toggleMarcado} fiscal>
        <input {...inp(`${pref}icms_red_bc_perc`, 'number', { step:'0.01', min:0, max:100 })} />
      </CF>
      <CF campo={`${pref}icms_aliq_perc`} label="Alíquota %" marcados={marcados} onToggle={toggleMarcado} fiscal>
        <input {...inp(`${pref}icms_aliq_perc`, 'number', { step:'0.01', min:0, max:100 })} />
      </CF>
    </>
  );

  // ── Seção ICMS-ST ────────────────────────────────────────────────────────
  const secIcmsSt = (pref) => (
    <>
      <div className={s.secLabel}>ICMS — Substituição Tributária</div>
      <CF campo={`${pref}icms_st_mod_bc`} label="Modalidade BC" marcados={marcados} onToggle={toggleMarcado} fiscal>
        <select {...sel(`${pref}icms_st_mod_bc`)}>
          <option value="">-- Selecione --</option>
          {MODS_BC_ICMS_ST.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      </CF>
      <CF campo={`${pref}icms_st_red_bc_perc`} label="Redução %" marcados={marcados} onToggle={toggleMarcado} fiscal>
        <input {...inp(`${pref}icms_st_red_bc_perc`, 'number', { step:'0.01', min:0, max:100 })} />
      </CF>
      <CF campo={`${pref}icms_st_mva_perc`} label="MVA %" marcados={marcados} onToggle={toggleMarcado} fiscal>
        <input {...inp(`${pref}icms_st_mva_perc`, 'number', { step:'0.01', min:0 })} />
      </CF>
      <CF campo={`${pref}icms_st_aliq_perc`} label="Alíquota %" marcados={marcados} onToggle={toggleMarcado} fiscal>
        <input {...inp(`${pref}icms_st_aliq_perc`, 'number', { step:'0.01', min:0, max:100 })} />
      </CF>
      <CF campo={`${pref}icms_st_ret_bc`} label="ST Retido – BC R$" marcados={marcados} onToggle={toggleMarcado} fiscal>
        <input {...inp(`${pref}icms_st_ret_bc`, 'number', { step:'0.01', min:0 })} />
      </CF>
      <CF campo={`${pref}icms_st_ret_valor`} label="ST Retido – Valor R$" marcados={marcados} onToggle={toggleMarcado} fiscal>
        <input {...inp(`${pref}icms_st_ret_valor`, 'number', { step:'0.01', min:0 })} />
      </CF>
    </>
  );

  // ── Seção IBS/CBS/IS ─────────────────────────────────────────────────────
  // `extras`: campos novos da reforma (CST/cClassTrib, redução de BC,
  // diferimento, crédito presumido) — só a Saída ganhou eles por enquanto
  // (é o lado que sai na nota; Entrada fica pra quando entrar o controle de
  // crédito/apuração). Entrada continua só com o que já existia.
  const secIbsCbs = (pref, extras = false) => (
    <>
      <div className={s.secLabel}>IBS / CBS / IS — Reforma Tributária</div>
      {extras && (
        <>
          <CF campo={`${pref}ibscbs_cst`} label="CST IBS/CBS" marcados={marcados} onToggle={toggleMarcado} fiscal>
            <select {...sel(`${pref}ibscbs_cst`)}>
              <option value="">-- Selecione --</option>
              {CSTS_IBSCBS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </CF>
          <CF campo={`${pref}ibscbs_cclasstrib`} label="cClassTrib" marcados={marcados} onToggle={toggleMarcado} fiscal>
            <input {...inp(`${pref}ibscbs_cclasstrib`, 'text', { maxLength: 6, placeholder: '000000' })} />
          </CF>
        </>
      )}
      <CF campo={`${pref}ibs_aliq_perc`} label="IBS – Alíquota %" marcados={marcados} onToggle={toggleMarcado} fiscal>
        <input {...inp(`${pref}ibs_aliq_perc`, 'number', { step:'0.0001', min:0 })} />
      </CF>
      <CF campo={`${pref}ibs_uf_destino`} label="IBS – UF Destino" marcados={marcados} onToggle={toggleMarcado} fiscal>
        <select {...sel(`${pref}ibs_uf_destino`)}>
          <option value="">-- Selecione --</option>
          {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
        </select>
      </CF>
      {extras && (
        <>
          <CF campo={`${pref}ibs_red_bc_perc`} label="IBS – Redução BC %" marcados={marcados} onToggle={toggleMarcado} fiscal>
            <input {...inp(`${pref}ibs_red_bc_perc`, 'number', { step:'0.01', min:0, max:100 })} />
          </CF>
          <CF campo={`${pref}ibs_dif_perc`} label="IBS – Diferimento %" marcados={marcados} onToggle={toggleMarcado} fiscal>
            <input {...inp(`${pref}ibs_dif_perc`, 'number', { step:'0.01', min:0, max:100 })} />
          </CF>
          <CF campo={`${pref}ibs_cred_pres_codigo`} label="IBS – Créd. Presumido (cód.)" marcados={marcados} onToggle={toggleMarcado} fiscal>
            <input {...inp(`${pref}ibs_cred_pres_codigo`, 'text', { maxLength: 10 })} />
          </CF>
          <CF campo={`${pref}ibs_cred_pres_perc`} label="IBS – Créd. Presumido %" marcados={marcados} onToggle={toggleMarcado} fiscal>
            <input {...inp(`${pref}ibs_cred_pres_perc`, 'number', { step:'0.0001', min:0 })} />
          </CF>
        </>
      )}
      <CF campo={`${pref}cbs_aliq_perc`} label="CBS – Alíquota %" marcados={marcados} onToggle={toggleMarcado} fiscal>
        <input {...inp(`${pref}cbs_aliq_perc`, 'number', { step:'0.0001', min:0 })} />
      </CF>
      {extras && (
        <>
          <CF campo={`${pref}cbs_red_bc_perc`} label="CBS – Redução BC %" marcados={marcados} onToggle={toggleMarcado} fiscal>
            <input {...inp(`${pref}cbs_red_bc_perc`, 'number', { step:'0.01', min:0, max:100 })} />
          </CF>
          <CF campo={`${pref}cbs_dif_perc`} label="CBS – Diferimento %" marcados={marcados} onToggle={toggleMarcado} fiscal>
            <input {...inp(`${pref}cbs_dif_perc`, 'number', { step:'0.01', min:0, max:100 })} />
          </CF>
          <CF campo={`${pref}cbs_cred_pres_codigo`} label="CBS – Créd. Presumido (cód.)" marcados={marcados} onToggle={toggleMarcado} fiscal>
            <input {...inp(`${pref}cbs_cred_pres_codigo`, 'text', { maxLength: 10 })} />
          </CF>
          <CF campo={`${pref}cbs_cred_pres_perc`} label="CBS – Créd. Presumido %" marcados={marcados} onToggle={toggleMarcado} fiscal>
            <input {...inp(`${pref}cbs_cred_pres_perc`, 'number', { step:'0.0001', min:0 })} />
          </CF>
        </>
      )}
      <CF campo={`${pref}is_incide`} label="IS – Incide" marcados={marcados} onToggle={toggleMarcado} fiscal>
        <select {...boo(`${pref}is_incide`)}>
          <option value="0">Não</option>
          <option value="1">Sim</option>
        </select>
      </CF>
      <CF campo={`${pref}is_aliq_perc`} label="IS – Alíquota %" marcados={marcados} onToggle={toggleMarcado} fiscal>
        <input {...inp(`${pref}is_aliq_perc`, 'number', { step:'0.0001', min:0 })} />
      </CF>
    </>
  );

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>📋</span> Alteração em Massa de Produtos</div>

      <div className={s.corpo}>
        {/* ── Coluna esquerda: filtros + grid ── */}
        <div className={s.colEsq}>
          <div className={s.filtrosSection}>
            <fieldset>
              <legend>Produtos para alteração</legend>
              <div className={s.filtroAcoes}>
                <button className={`${s.btn} ${s.btnBuscar}`} onClick={buscaF3.abrirF3}>
                  🔍 Buscar produtos
                </button>
                <button className={`${s.btn} ${s.btnLimpar}`} onClick={limparLista} disabled={produtos.length === 0}>
                  Limpar lista
                </button>
                <span style={{ fontSize: 11, color: '#666', marginLeft: 4 }}>
                  Cada busca ACRESCENTA os produtos encontrados à lista abaixo — repita com outro filtro pra somar mais.
                </span>
              </div>
            </fieldset>
          </div>

          <div className={s.gridToolbar}>
            {buscaAtivada ? (
              <>
                <span><strong>{produtos.length}</strong> na lista &nbsp;|&nbsp; <strong>{qtdSelecionados}</strong> selecionado(s)</span>
                <button className={`${s.btn} ${s.btnLimpar}`} onClick={selecionarTodos} disabled={produtos.length === 0}>Sel. todos</button>
                <button className={`${s.btn} ${s.btnLimpar}`} onClick={desmarcarTodos} disabled={qtdSelecionados === 0}>Desmarcar</button>
              </>
            ) : (
              <span style={{ color:'#666', fontStyle:'italic' }}>Clique em "Buscar produtos" pra montar a lista.</span>
            )}
          </div>

          <div className={s.gridWrap}>
            {buscaAtivada && produtos.length === 0 ? (
              <div className={s.semResultado}>Nenhum produto encontrado.</div>
            ) : (
              <table className={s.grid}>
                <thead>
                  <tr>
                    <th>
                      <input type="checkbox" checked={todosSelecionados}
                        onChange={todosSelecionados ? desmarcarTodos : selecionarTodos}
                        disabled={produtos.length === 0} />
                    </th>
                    <th>Código</th>
                    <th>Descrição</th>
                    <th>Tipo</th>
                    <th>Grupo</th>
                    <th>Un.</th>
                    <th style={{ textAlign:'right' }}>Pr. Venda</th>
                  </tr>
                </thead>
                <tbody>
                  {produtos.map(p => (
                    <tr key={p.id} className={selecionados.has(p.id) ? s.selecionado : ''}
                      onClick={() => toggleSelecionado(p.id)} style={{ cursor:'pointer' }}>
                      <td onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selecionados.has(p.id)}
                          onChange={() => toggleSelecionado(p.id)} />
                      </td>
                      <td>{p.codigo_interno}</td>
                      <td>{p.nome}</td>
                      <td>{p.tipo}</td>
                      <td>{p.grupo_nome}</td>
                      <td>{p.unidade}</td>
                      <td style={{ textAlign:'right' }}>
                        {p.preco_venda != null
                          ? Number(p.preco_venda).toLocaleString('pt-BR', { minimumFractionDigits:2 })
                          : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ── Coluna direita: painel de alterações ── */}
        <div className={s.colDir}>
          <div className={s.painelHeader}>
            {[['geral','Geral'],['fiscal','Fiscal'],['outros','Outros']].map(([id, lbl]) => (
              <button key={id} className={`${s.tab} ${abaAtiva === id ? s.tabAtiva : ''}`}
                onClick={() => setAbaAtiva(id)}>{lbl}</button>
            ))}
          </div>

          <div className={s.tabContent}>
            <div style={{ marginBottom:6, fontSize:11, color:'#555' }}>
              Marque os campos que deseja alterar nos produtos selecionados:
            </div>

            {/* ── ABA GERAL ── */}
            {abaAtiva === 'geral' && (
              <>
                <CF campo="fornecedor_id" label="Fornecedor" marcados={marcados} onToggle={toggleMarcado}>
                  <select {...sel('fornecedor_id')}>
                    <option value="">-- Nenhum --</option>
                    {fornecedores.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                  </select>
                </CF>
                <CF campo="marca_id" label="Marca" marcados={marcados} onToggle={toggleMarcado}>
                  <select {...sel('marca_id')}>
                    <option value="">-- Nenhuma --</option>
                    {marcas.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
                  </select>
                </CF>
                <CF campo="grupo_id" label="Grupo" marcados={marcados} onToggle={toggleMarcado}>
                  <select {...sel('grupo_id')}>
                    <option value="">-- Nenhum --</option>
                    {grupos.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
                  </select>
                </CF>
                <CF campo="subgrupo_id" label="Subgrupo" marcados={marcados} onToggle={toggleMarcado}>
                  <select {...sel('subgrupo_id')}>
                    <option value="">-- Nenhum --</option>
                    {subgrupos.map(sg => <option key={sg.id} value={sg.id}>{sg.nome}</option>)}
                  </select>
                </CF>
                <CF campo="tipo" label="Tipo" marcados={marcados} onToggle={toggleMarcado}>
                  <select {...sel('tipo')}>
                    <option value="">-- Selecione --</option>
                    {TIPOS_PRODUTO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </CF>
                <CF campo="inativo" label="Inativo" marcados={marcados} onToggle={toggleMarcado}>
                  <select {...boo('inativo')}>
                    <option value="0">Não (ativo)</option>
                    <option value="1">Sim (inativo)</option>
                  </select>
                </CF>

                {/* Atributos — mesmo grupo/nomes da seção "Atributos" do Cad.
                    Produtos (CadastroProdutos.jsx), reunidos aqui num só
                    lugar em vez de espalhados entre abas. */}
                <CF campo="pesado_no_caixa" label="Pesado no caixa" marcados={marcados} onToggle={toggleMarcado}>
                  <select {...boo('pesado_no_caixa')}><option value="0">Não</option><option value="1">Sim</option></select>
                </CF>
                <CF campo="possui_acrescimo" label="Possui acréscimo" marcados={marcados} onToggle={toggleMarcado}>
                  <select {...boo('possui_acrescimo')}><option value="0">Não</option><option value="1">Sim</option></select>
                </CF>
                <CF campo="producao_propria" label="Produção própria" marcados={marcados} onToggle={toggleMarcado}>
                  <select {...boo('producao_propria')}><option value="0">Não</option><option value="1">Sim</option></select>
                </CF>
                <CF campo="produto_balanca" label="Produto de balança" marcados={marcados} onToggle={toggleMarcado}>
                  <select {...boo('produto_balanca')}><option value="0">Não</option><option value="1">Sim</option></select>
                </CF>
                <CF campo="recebido_em_consignacao" label="Recebido em consignação" marcados={marcados} onToggle={toggleMarcado}>
                  <select {...boo('recebido_em_consignacao')}><option value="0">Não</option><option value="1">Sim</option></select>
                </CF>
                <CF campo="permite_alterar_qtd_preco" label="Permite alterar quantidade/preço no PDV" marcados={marcados} onToggle={toggleMarcado}>
                  <select {...boo('permite_alterar_qtd_preco')}><option value="0">Não</option><option value="1">Sim</option></select>
                </CF>
                <CF campo="item_cozinha" label="Item de Cozinha" marcados={marcados} onToggle={toggleMarcado}>
                  <select {...boo('item_cozinha')}><option value="0">Não</option><option value="1">Sim</option></select>
                </CF>
                <CF campo="marcador" label="Marcador" marcados={marcados} onToggle={toggleMarcado}>
                  <select {...boo('marcador')}><option value="0">Não</option><option value="1">Sim</option></select>
                </CF>
                <CF campo="composto" label="Composto" marcados={marcados} onToggle={toggleMarcado}>
                  <select {...boo('composto')}><option value="0">Não</option><option value="1">Sim</option></select>
                </CF>
              </>
            )}

            {/* ── ABA FISCAL ── */}
            {abaAtiva === 'fiscal' && (
              <>
                {/* Campos do produto */}
                <div className={s.secProdutoLabel}>Campos do Produto</div>
                <CF campo="ncm" label="NCM" marcados={marcados} onToggle={toggleMarcado} fiscal>
                  <input {...inp('ncm')} maxLength={8} placeholder="00000000" />
                </CF>
                <CF campo="cest" label="CEST" marcados={marcados} onToggle={toggleMarcado} fiscal>
                  <input {...inp('cest')} maxLength={7} placeholder="0000000" />
                </CF>
                <CF campo="origem_fiscal" label="Origem" marcados={marcados} onToggle={toggleMarcado} fiscal>
                  <select disabled={!marcados.origem_fiscal}
                    value={valores.origem_fiscal ?? 0}
                    onChange={e => setV('origem_fiscal', parseInt(e.target.value))}>
                    {ORIGENS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </CF>
                <CF campo="tipo_fiscal" label="Tipo Fiscal" marcados={marcados} onToggle={toggleMarcado} fiscal>
                  <select {...sel('tipo_fiscal')}>
                    {TIPO_FISCAL_OPTS.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                </CF>
                <CF campo="unidade_tributavel" label="Un. Tributável" marcados={marcados} onToggle={toggleMarcado} fiscal>
                  <input {...inp('unidade_tributavel')} maxLength={6} placeholder="UN" />
                </CF>
                <CF campo="fator_conversao_trib" label="Fator Conv. Trib." marcados={marcados} onToggle={toggleMarcado} fiscal>
                  <input type="number" step="0.0001" min="0"
                    disabled={!marcados.fator_conversao_trib}
                    value={valores.fator_conversao_trib ?? 1}
                    onChange={e => setV('fator_conversao_trib', e.target.value)} />
                </CF>
                <CF campo="unidade_tributavel_diferente" label="Un. Trib. Diferente" marcados={marcados} onToggle={toggleMarcado} fiscal>
                  <select {...boo('unidade_tributavel_diferente')}>
                    <option value="0">Não</option>
                    <option value="1">Sim</option>
                  </select>
                </CF>

                {/* Sub-abas Entrada / Saída */}
                <div style={{ marginTop:10 }}>
                  <div className={s.subTabBar}>
                    <button className={`${s.subTab} ${subAbaFiscal === 'entrada' ? s.subTabAtivo : ''}`}
                      onClick={() => setSubAbaFiscal('entrada')}>Entrada</button>
                    <button className={`${s.subTab} ${subAbaFiscal === 'saida' ? s.subTabAtivo : ''}`}
                      onClick={() => setSubAbaFiscal('saida')}>Saída</button>
                  </div>

                  {subAbaFiscal === 'entrada' && (
                    <>
                      {secIcms('fe_', CSTS_ICMS)}
                      {secIcmsSt('fe_')}

                      <div className={s.secLabel}>PIS</div>
                      <CF campo="fe_pis_cst" label="Situação Trib." marcados={marcados} onToggle={toggleMarcado} fiscal>
                        <select {...sel('fe_pis_cst')}>
                          <option value="">-- Selecione --</option>
                          {CSTS_PIS_COFINS_ENT.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                      </CF>
                      <CF campo="fe_pis_aliq_perc" label="Alíquota %" marcados={marcados} onToggle={toggleMarcado} fiscal>
                        <input {...inp('fe_pis_aliq_perc', 'number', { step:'0.01', min:0, max:100 })} />
                      </CF>

                      <div className={s.secLabel}>COFINS</div>
                      <CF campo="fe_cofins_cst" label="Situação Trib." marcados={marcados} onToggle={toggleMarcado} fiscal>
                        <select {...sel('fe_cofins_cst')}>
                          <option value="">-- Selecione --</option>
                          {CSTS_PIS_COFINS_ENT.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                      </CF>
                      <CF campo="fe_cofins_aliq_perc" label="Alíquota %" marcados={marcados} onToggle={toggleMarcado} fiscal>
                        <input {...inp('fe_cofins_aliq_perc', 'number', { step:'0.01', min:0, max:100 })} />
                      </CF>

                      {secIbsCbs('fe_')}
                    </>
                  )}

                  {subAbaFiscal === 'saida' && (
                    <>
                      {secIcms('fs_', CSTS_ICMS)}

                      <CF campo="fs_icms_aliq_efet_perc" label="Alíq. Efetiva %" marcados={marcados} onToggle={toggleMarcado} fiscal>
                        <input {...inp('fs_icms_aliq_efet_perc', 'number', { step:'0.01', min:0, max:100 })} />
                      </CF>
                      <CF campo="fs_icms_dif_perc" label="Diferimento %" marcados={marcados} onToggle={toggleMarcado} fiscal>
                        <input {...inp('fs_icms_dif_perc', 'number', { step:'0.01', min:0, max:100 })} />
                      </CF>

                      {secIcmsSt('fs_')}

                      <div className={s.secLabel}>PIS</div>
                      <CF campo="fs_pis_cst" label="Situação Trib." marcados={marcados} onToggle={toggleMarcado} fiscal>
                        <select {...sel('fs_pis_cst')}>
                          <option value="">-- Selecione --</option>
                          {CSTS_PIS_COFINS_SAI.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                      </CF>
                      <CF campo="fs_pis_aliq_perc" label="Alíquota %" marcados={marcados} onToggle={toggleMarcado} fiscal>
                        <input {...inp('fs_pis_aliq_perc', 'number', { step:'0.01', min:0, max:100 })} />
                      </CF>
                      <CF campo="fs_pis_nat_rec" label="Nat. Receita" marcados={marcados} onToggle={toggleMarcado} fiscal>
                        <input {...inp('fs_pis_nat_rec')} maxLength={3} placeholder="000" />
                      </CF>

                      <div className={s.secLabel}>COFINS</div>
                      <CF campo="fs_cofins_cst" label="Situação Trib." marcados={marcados} onToggle={toggleMarcado} fiscal>
                        <select {...sel('fs_cofins_cst')}>
                          <option value="">-- Selecione --</option>
                          {CSTS_PIS_COFINS_SAI.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                      </CF>
                      <CF campo="fs_cofins_aliq_perc" label="Alíquota %" marcados={marcados} onToggle={toggleMarcado} fiscal>
                        <input {...inp('fs_cofins_aliq_perc', 'number', { step:'0.01', min:0, max:100 })} />
                      </CF>
                      <CF campo="fs_cofins_nat_rec" label="Nat. Receita" marcados={marcados} onToggle={toggleMarcado} fiscal>
                        <input {...inp('fs_cofins_nat_rec')} maxLength={3} placeholder="000" />
                      </CF>

                      {secIbsCbs('fs_', true)}
                    </>
                  )}
                </div>
              </>
            )}

            {/* ── ABA OUTROS ── */}
            {abaAtiva === 'outros' && (
              <>
                <CF campo="peso_kg" label="Peso (kg)" marcados={marcados} onToggle={toggleMarcado}>
                  <input type="number" step="0.001" min="0"
                    disabled={!marcados.peso_kg} value={valores.peso_kg ?? ''}
                    onChange={e => setV('peso_kg', e.target.value)} />
                </CF>
                <CF campo="comissao_pct" label="Comissão %" marcados={marcados} onToggle={toggleMarcado}>
                  <input type="number" step="0.01" min="0" max="100"
                    disabled={!marcados.comissao_pct} value={valores.comissao_pct ?? ''}
                    onChange={e => setV('comissao_pct', e.target.value)} />
                </CF>
                <CF campo="desconto_maximo_pct" label="Desc. Máximo %" marcados={marcados} onToggle={toggleMarcado}>
                  <input type="number" step="0.01" min="0" max="100"
                    disabled={!marcados.desconto_maximo_pct} value={valores.desconto_maximo_pct ?? ''}
                    onChange={e => setV('desconto_maximo_pct', e.target.value)} />
                </CF>
                <CF campo="margem_pct" label="Margem %" marcados={marcados} onToggle={toggleMarcado}>
                  <input type="number" step="0.01" min="0"
                    disabled={!marcados.margem_pct} value={valores.margem_pct ?? ''}
                    onChange={e => setV('margem_pct', e.target.value)} />
                </CF>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className={s.footer}>
        <div className={s.statusBar}>
          {statusTipo === 'erro' && <span className={s.statusErro}>⚠ {status}</span>}
          {statusTipo === 'ok'   && <span className={s.statusOk}>✔ {status}</span>}
          {!statusTipo && status && <span>{status}</span>}
          {!status && <span style={{ color:'#888' }}>Pronto.</span>}
        </div>
        <div className={s.footerBtns}>
          <button className={`${s.btn} ${s.btnCancelar}`} onClick={limparSelecao}>Limpar Seleção</button>
          <button className={`${s.btn} ${s.btnAplicar}`}
            onClick={confirmarAplicar}
            disabled={qtdSelecionados === 0 || camposMarcados.length === 0 || alteracaoMutation.isPending}>
            {alteracaoMutation.isPending
              ? 'Aplicando…'
              : `Aplicar em ${qtdSelecionados} produto${qtdSelecionados !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>

      {/* ── Modal de confirmação ── */}
      {confirmando && (() => {
        const doProd = camposMarcados.filter(c => !c.startsWith('fe_') && !c.startsWith('fs_'));
        const doEnt  = camposMarcados.filter(c => c.startsWith('fe_'));
        const doSai  = camposMarcados.filter(c => c.startsWith('fs_'));
        return (
          <div className={s.modalOverlay}>
            <div className={s.modalBox}>
              <div className={s.modalTitleBar}>Confirmar Alteração em Massa</div>
              <div className={s.modalBody}>
                <p>Alterar <strong>{qtdSelecionados} produto(s)</strong> nos seguintes campos:</p>
                {doProd.length > 0 && (
                  <>
                    <p style={{ margin:'4px 0 2px', fontWeight:'bold', fontSize:11 }}>Dados do produto:</p>
                    <ul style={{ margin:'0 0 6px 16px', fontSize:11 }}>
                      {doProd.map(c => <li key={c} style={{ listStyle:'disc' }}>{LABELS[c] || c}</li>)}
                    </ul>
                  </>
                )}
                {doEnt.length > 0 && (
                  <>
                    <p style={{ margin:'4px 0 2px', fontWeight:'bold', fontSize:11 }}>Fiscal — Entrada ({doEnt.length} campo{doEnt.length>1?'s':''}):</p>
                    <ul style={{ margin:'0 0 6px 16px', fontSize:11 }}>
                      {doEnt.map(c => <li key={c} style={{ listStyle:'disc' }}>{LABELS[c] || c}</li>)}
                    </ul>
                  </>
                )}
                {doSai.length > 0 && (
                  <>
                    <p style={{ margin:'4px 0 2px', fontWeight:'bold', fontSize:11 }}>Fiscal — Saída ({doSai.length} campo{doSai.length>1?'s':''}):</p>
                    <ul style={{ margin:'0 0 6px 16px', fontSize:11 }}>
                      {doSai.map(c => <li key={c} style={{ listStyle:'disc' }}>{LABELS[c] || c}</li>)}
                    </ul>
                  </>
                )}
                <p style={{ color:'#c00', marginTop:8 }}>Esta operação não pode ser desfeita. Confirma?</p>
              </div>
              <div className={s.modalFooter}>
                <button className={`${s.btn} ${s.btnAplicar}`} onClick={executarAlteracao}>Sim, alterar</button>
                <button className={`${s.btn} ${s.btnCancelar}`} onClick={() => setConfirmando(false)}>Cancelar</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── F3 — Busca Avançada de Produtos (modo "usar todos") ── */}
      <ModalBuscaProdutos
        modalF3={buscaF3.modalF3} fecharF3={() => buscaF3.setModalF3(false)}
        filtrosF3={buscaF3.filtrosF3} setFiltro={buscaF3.setFiltro} setFiltrosF3={buscaF3.setFiltrosF3}
        FILTROS_VAZIOS={FILTROS_F3_VAZIOS}
        produtosF3={buscaF3.produtosF3} setProdutosF3={buscaF3.setProdutosF3}
        buscaRapidaF3={buscaF3.buscaRapidaF3} setBuscaRapidaF3={buscaF3.setBuscaRapidaF3}
        handleBuscaRapidaF3={buscaF3.handleBuscaRapidaF3}
        buscandoF3={buscaF3.buscandoF3} buscarF3={buscaF3.buscarF3}
        idxF3={buscaF3.idxF3} setIdxF3={buscaF3.setIdxF3}
        buscaRapidaRef={buscaF3.buscaRapidaRef}
        modoTodos confirmarTodos={confirmarTodos}
        avancado
      />
    </div>
  );
}
